/**
 * Contractor self-service time entry portal.
 *
 * A simple, mobile-friendly page where non-Oorwin contractors enter
 * their daily hours, upload client-approval screenshots, and track
 * submission status. Completely separate from the admin workspace.
 */

import { useState } from 'react';

import { api } from './api';
import { Card, CardHeader, Spinner } from './components/Ui';
import { useAsyncData } from './hooks/useAsyncData';
import { shortDate } from './format';
import { Shell, type ShellProps } from './Shell';

const CATEGORIES = [
  { value: 'regular', label: 'Regular' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'vacation', label: 'Vacation' },
] as const;

function daysBetween(start: string, end: string): string[] {
  const days: string[] = [];
  const s = new Date(start + 'T12:00:00Z');
  const e = new Date(end + 'T12:00:00Z');
  while (s <= e) {
    days.push(s.toISOString().slice(0, 10));
    s.setUTCDate(s.getUTCDate() + 1);
  }
  return days;
}

function dayName(iso: string): string {
  return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short' });
}

export function Portal(props: ShellProps) {
  const { data: periodsData, error: periodsError } = useAsyncData(
    () => api.portalMyPeriods(),
    [],
    'Could not load pay periods.',
  );

  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);

  if (periodsError) {
    const isNotLinked =
      periodsError.includes('not linked') || periodsError.includes('account_not_linked');
    return (
      <Shell {...props}>
        <Card>
          <div className="px-4 py-8 text-center">
            {isNotLinked ? (
              <>
                <p className="text-lg font-semibold text-ink mb-2">Account Setup Needed</p>
                <p className="text-sm text-ink-muted">
                  Your account hasn't been linked to your contractor record yet. Please contact Sue
                  to complete your account setup.
                </p>
              </>
            ) : (
              <p className="text-red-600">{periodsError}</p>
            )}
          </div>
        </Card>
      </Shell>
    );
  }

  if (!periodsData) {
    return (
      <Shell {...props}>
        <div className="flex justify-center py-12">
          <Spinner label="Loading periods…" />
        </div>
      </Shell>
    );
  }

  const activePeriod = selectedPeriod
    ? periodsData.periods.find((p) => p.id === selectedPeriod)
    : periodsData.periods[0];

  return (
    <Shell {...props}>
      <div className="mb-4">
        <h1 className="text-lg font-semibold text-ink">Time Entry</h1>
        <p className="text-sm text-ink-muted">Enter your hours for each day of the pay period.</p>
      </div>

      {periodsData.periods.length > 1 && (
        <div className="mb-4">
          <select
            value={activePeriod?.id ?? ''}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="rounded-lg border border-edge bg-surface-raised px-3 py-1.5 text-sm"
          >
            {periodsData.periods.map((p) => (
              <option key={p.id} value={p.id}>
                Week ending {shortDate(p.weekEnding)} ({p.status})
              </option>
            ))}
          </select>
        </div>
      )}

      {activePeriod ? (
        <TimeEntryGrid
          periodId={activePeriod.id}
          periodStart={activePeriod.periodStart}
          periodEnd={activePeriod.periodEnd}
        />
      ) : (
        <Card>
          <div className="px-4 py-8 text-center text-ink-muted">No open pay periods available.</div>
        </Card>
      )}

      <SubmissionHistory />
    </Shell>
  );
}

function TimeEntryGrid({
  periodId,
  periodStart,
  periodEnd,
}: {
  periodId: string;
  periodStart: string;
  periodEnd: string;
}) {
  const days = daysBetween(periodStart, periodEnd);
  const [entries, setEntries] = useState<Record<string, { category: string; hours: string }>>(
    () => {
      const init: Record<string, { category: string; hours: string }> = {};
      for (const d of days) init[d] = { category: 'regular', hours: '' };
      return init;
    },
  );
  const [screenshotKey, setScreenshotKey] = useState<string | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: existing } = useAsyncData(() => api.portalMyHours(periodId), [periodId]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    // Show local preview immediately
    setScreenshotPreview(URL.createObjectURL(file));
    try {
      const result = await api.portalUploadScreenshot(file);
      setScreenshotKey(result.key);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
      setScreenshotPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    setSubmitResult(null);
    try {
      const entryList = Object.entries(entries)
        .filter(([, v]) => v.hours && Number(v.hours) > 0)
        .map(([date, v]) => ({
          workDate: date,
          earningCategory: v.category,
          hours: Number(v.hours),
        }));

      if (entryList.length === 0) {
        setError('Enter hours for at least one day.');
        setSubmitting(false);
        return;
      }

      const result = await api.portalSubmitHours({
        periodId,
        entries: entryList,
        ...(screenshotKey !== null ? { screenshotKey } : {}),
      });
      setSubmitResult(`Submitted ${String(result.submitted)} entries for review.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const hasExistingSubmitted = existing?.entries.some(
    (e) => e.status === 'submitted' || e.status === 'approved',
  );

  return (
    <Card>
      <CardHeader
        title={`${shortDate(periodStart)} – ${shortDate(periodEnd)}`}
        subtitle={
          hasExistingSubmitted
            ? 'Hours already submitted for this period'
            : 'Enter your hours below'
        }
      />

      <div className="px-4 pb-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-edge">
              <th className="px-2 py-2 text-left text-xs font-medium text-ink-muted">Day</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-ink-muted">Date</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-ink-muted">Category</th>
              <th className="px-2 py-2 text-right text-xs font-medium text-ink-muted">Hours</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => {
              const isWeekend = [0, 6].includes(new Date(d + 'T12:00:00Z').getUTCDay());
              const existingEntry = existing?.entries.find((e) => e.workDate === d);
              return (
                <tr
                  key={d}
                  className={`border-b border-edge/30 ${isWeekend ? 'bg-surface-overlay/30' : ''}`}
                >
                  <td className="px-2 py-1.5 font-medium">{dayName(d)}</td>
                  <td className="px-2 py-1.5 text-ink-muted">{shortDate(d)}</td>
                  <td className="px-2 py-1.5">
                    {existingEntry && existingEntry.status !== 'rejected' ? (
                      <span className="text-ink-faint">{existingEntry.earningCategory}</span>
                    ) : (
                      <select
                        value={entries[d]?.category ?? 'regular'}
                        onChange={(e) =>
                          setEntries({
                            ...entries,
                            [d]: { ...entries[d]!, category: e.target.value },
                          })
                        }
                        className="rounded border border-edge bg-surface-raised px-1.5 py-1 text-xs"
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {existingEntry && existingEntry.status !== 'rejected' ? (
                      <span className="font-mono">{existingEntry.hours}</span>
                    ) : (
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="24"
                        value={entries[d]?.hours ?? ''}
                        onChange={(e) =>
                          setEntries({ ...entries, [d]: { ...entries[d]!, hours: e.target.value } })
                        }
                        placeholder="0"
                        className="w-16 rounded border border-edge bg-surface-raised px-2 py-1 text-right font-mono text-sm"
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Screenshot upload — file picker OR paste from clipboard */}
        <div
          className={`mt-4 rounded-lg border-2 border-dashed p-4 transition-colors ${
            screenshotKey ? 'border-profit/30 bg-profit/5' : 'border-edge hover:border-ink-faint'
          }`}
          onPaste={(e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (const item of items) {
              if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (file) void handleUpload(file);
                return;
              }
            }
          }}
          tabIndex={0}
        >
          {screenshotKey || screenshotPreview ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-profit font-medium">
                  {uploading ? 'Uploading…' : 'Screenshot attached'}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setScreenshotKey(null);
                    if (screenshotPreview) {
                      URL.revokeObjectURL(screenshotPreview);
                      setScreenshotPreview(null);
                    }
                  }}
                  className="text-xs text-ink-faint hover:text-ink-muted"
                >
                  Remove
                </button>
              </div>
              {screenshotPreview && (
                <img
                  src={screenshotPreview}
                  alt="Uploaded screenshot"
                  className="max-h-48 rounded-lg border border-edge object-contain"
                />
              )}
            </div>
          ) : (
            <div className="text-center">
              <p className="text-xs font-medium text-ink-muted mb-2">
                Screenshot of client-approved hours (optional but recommended)
              </p>
              <div className="flex items-center justify-center gap-3">
                <label className="cursor-pointer">
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-edge bg-surface-overlay px-3 py-1.5 text-xs font-medium text-ink-muted hover:text-ink transition-colors">
                    {uploading ? 'Uploading…' : 'Choose file'}
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void handleUpload(f);
                    }}
                  />
                </label>
                <span className="text-xs text-ink-faint">or paste from clipboard (Ctrl+V)</span>
              </div>
            </div>
          )}
        </div>

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
        {submitResult && <p className="mt-3 text-xs text-profit">{submitResult}</p>}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            disabled={submitting || hasExistingSubmitted === true}
            onClick={handleSubmit}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              submitting || hasExistingSubmitted
                ? 'bg-ink-faint/20 text-ink-muted cursor-not-allowed'
                : 'bg-profit text-white hover:bg-profit/90'
            }`}
          >
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </Card>
  );
}

function SubmissionHistory() {
  const { data } = useAsyncData(() => api.portalMySubmissions(), []);

  if (!data || data.submissions.length === 0) return null;

  const statusColor: Record<string, string> = {
    draft: 'text-ink-faint',
    submitted: 'text-caution',
    approved: 'text-profit',
    rejected: 'text-red-600',
  };

  return (
    <Card className="mt-4">
      <CardHeader title="Submission History" />
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-edge">
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-muted">Week Ending</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-ink-muted">Entries</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-ink-muted">Hours</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-ink-muted">Status</th>
          </tr>
        </thead>
        <tbody>
          {data.submissions.map((s, i) => (
            <tr key={i} className="border-b border-edge/30">
              <td className="px-4 py-1.5">{shortDate(s.weekEnding)}</td>
              <td className="px-4 py-1.5 text-right font-mono">{String(s.entryCount)}</td>
              <td className="px-4 py-1.5 text-right font-mono">{s.totalHours}</td>
              <td className={`px-4 py-1.5 font-medium ${statusColor[s.status] ?? ''}`}>
                {s.status}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
